import { PUBLISH_VERSION, TRACK_API_URL } from 'constants/index';
import {
  OS,
  Genre,
  TGame,
  Feature,
  TQueryParams,
  TQueryParamKey,
  TBackendFeature,
  TBackendResponse,
} from 'types';

const SOURCE_TYPE_PLACEHOLDER = '__SOURCE_TYPE__';
const SOURCE_PARAMS_PLACEHOLDER = '__SOURCE_PARAMS__';

export const closeWebView = async () => {
  try {
    if ('Android' in window) {
      (window.Android as any).onClose();
    } else {
      if ('webkit' in window) {
        (window.webkit as any).messageHandlers.hideListener.postMessage('close');
      } else {
        console.error('Run the app in the webview! No handler found!');
      }
    }

    await trackClose();
  } catch (error) {
    console.error('Error during the close method:', error);
  }
};

export const openStore = (gameId: string) => {
  try {
    if ('Android' in window) {
      (window.Android as any).onOpenStore(gameId);
    } else {
      if ('webkit' in window) {
        (window.webkit as any).messageHandlers.openStoreListener.postMessage(gameId);
      } else {
        console.error('Run the app in the webview! No handler found!');
      }
    }
  } catch (error) {
    console.error('Error during the open store method:', error);
  }
};

export const getOS = (): OS | undefined => {
  try {
    if ('Android' in window) {
      return OS.android;
    } else {
      if ('webkit' in window) {
        return OS.ios;
      } else {
        return undefined;
      }
    }
  } catch (error) {
    return undefined;
  }
};

export const getOSByUA = (): OS => {
  const userAgent = navigator.userAgent || (window as any).opera;
  const IPAD_PRO_CHECK = (/(Macintosh)/i.test(userAgent) && navigator.maxTouchPoints > 0);

  if (/android/i.test(userAgent)) {
    return OS.android;
  }

  if ((/iPad|iPhone|iPod/.test(userAgent) || IPAD_PRO_CHECK) && !(window as any).MSStream) {
    return OS.ios;
  }

  return OS.unknown;
}

export const getQueryStringParams = (): TQueryParams => {
  const searchParams = new URLSearchParams(window.location.search);

  return {
    idfa: searchParams.get('idfa'),
    idfv: searchParams.get('device_id'),
    appkey: searchParams.get('appkey'),
    lang: searchParams.get('lng'),
    country: searchParams.get('country') || searchParams.get('country_code'),
    saykit_version: searchParams.get('saykit'),
    app_version: searchParams.get('version'),
    install_date: searchParams.get('install_date'),
  };
};

export const prepareQueryStringParams = (params: TQueryParams): URLSearchParams => {
  const preparedParams = Object.keys(params).reduce((res, param) => {
    const key = param as TQueryParamKey;

    if (params[key]) {
      return { ...res, [key]: params[key] };
    }

    return res;
  }, {});

  return new URLSearchParams(preparedParams);
};

export const getRequestParams = (excludeKeys?: TQueryParamKey[]): URLSearchParams => {
  const queryParams = getQueryStringParams();

  let filteredQueryParams = {} as TQueryParams;

  Object.entries({ ...queryParams, os: getOS() }).forEach(([key, value]) => {
    const param = key as TQueryParamKey;

    if (!excludeKeys?.includes(param)) {
      filteredQueryParams[param] = value;
    }
  });

  return prepareQueryStringParams({
    ...filteredQueryParams,
  });
};

export const mapBackendFeatureToFeature = (backendFeature: TBackendFeature): Feature => {
  return {
    logoURL: backendFeature.icon_url,
    title: backendFeature.title,
    subtitle: backendFeature.subtitle,
  };
};

export const mapBackendDataToGames = (data: TBackendResponse): TGame[] => {
  return data.items.map((gameItem) => {
    if (!gameItem.genres?.length) return null;

    return {
      id: gameItem.store_id,
      name: gameItem.Name,
      title: gameItem.title,
      description: gameItem.description,
      reviewsCount: gameItem.reviews_count,
      rating: gameItem.rating,
      genre: gameItem.genres[0] as Genre,
      logoURL: gameItem.icon_url,
      screenshots: gameItem.screenshots,
      tags: gameItem.tags || [],
      features: gameItem.features?.map((feature) => mapBackendFeatureToFeature(feature)) || [],
      trackURL: gameItem.track_urls,
      video: gameItem.video_url,
      videoShort: gameItem.video_url_short,
      videoPoster: gameItem.video_poster_url,
      isGameOfDay: gameItem.is_game_of_the_day,
      isLookALike: gameItem.is_lookalike,
      gamesLikeThis: gameItem.games_like_this,
    };
  }).filter(Boolean) as TGame[];
};

export const trackEvent = async (url: string) => {
  // Replace placeholders with values which were saved in trackShow method call from outside on the React App.
  const preparedURL = url
    .replace(SOURCE_PARAMS_PLACEHOLDER, (window as any).sourceParams)
    .replace(SOURCE_TYPE_PLACEHOLDER, (window as any).sourceType);

  try {
    await fetch(preparedURL);
  } catch (e) {
    console.error(`Failed to track event! URL: ${preparedURL}`)
  }
};

export const trackError = async (error: any) => {
  const extraParams = {
    event: 'error',
    extra: error.toString().slice(0, 255),
  };

  const requestParams = getRequestParams();
  const errorParams = new URLSearchParams(extraParams);

  const url = TRACK_API_URL + `?${errorParams}&${requestParams}`;

  await trackEvent(url);
};

export const trackClose = async () => {
  const extraParams = {
    event: 'close',
    source_params: SOURCE_PARAMS_PLACEHOLDER,
    source_type: SOURCE_TYPE_PLACEHOLDER,
    request_id: (window as any).request_id,
    ab_test_id: (window as any).ab_test_id,
    last_scroll: (window as any).last_scroll,
  };

  const requestParams = getRequestParams();
  const closeParams = new URLSearchParams(extraParams);

  const url = TRACK_API_URL + `?${closeParams}&${requestParams}`;

  await trackEvent(url);
};

// WARN: Provide trackShow method to use it outside from WebView.
(window as any).trackShow = async (sourceType: string, params: string) => {
  const extraParams = {
    event: 'show',
    source_params: params,
    source_type: sourceType,
    request_id: (window as any).request_id,
    ab_test_id: (window as any).ab_test_id,
    publish_version: PUBLISH_VERSION,
  };

  // Save source values for upcoming tracking events. Use window object here to overcome issues with localStorage and
  // cookies in the WebView.
  (window as any).sourceParams = params;
  (window as any).sourceType = sourceType;

  // If there is no request_id at the moment when trackShow was called, then we will re-call it once request_id is set.
  if (!(window as any).request_id) {
    (window as any).shouldTrackShow = true;

    return;
  }

  const requestParams = getRequestParams();
  const showParams = new URLSearchParams(extraParams);

  const url = TRACK_API_URL + `?${showParams}&${requestParams}`;

  await trackEvent(url);
};

export const htmlDecode = (str: string) => {
  let text = new DOMParser().parseFromString(str, 'text/html');

  return text.documentElement.textContent;
};

export const getChunks = (arr: any[], chunkSize: number = 7) => Array.from(
  new Array(Math.ceil(arr.length / chunkSize)),
  (_, i) => arr.slice(i * chunkSize, i * chunkSize + chunkSize)
);
